import PropTypes from 'prop-types';
import { BsBuilding, BsGeoAlt, BsTelephone, BsEnvelope, BsGlobe, BsX, BsArrowRight, BsPlus } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import IconButton from '#globalComponents/IconButton/IconButton';
import Tag from '#globalComponents/Tag/Tag';
import { getGermanLabel } from '#lib/constants/contactRoles';
import { getInitials } from '#lib/textUtils';

import { useCompanyDetail } from '../../../models/companies/queries';
import { formatEuro, formatLossReason } from '../../../utils/formatters';
import CustomFieldsSection from '../../common/CustomFieldsSection/CustomFieldsSection';

// Drawer Field

const DrawerField = ({ icon: Icon, label, value, children }) => (
	<div className="flex justify-between items-start text-xs gap-2">
		<span className="text-bgBase-800 flex items-center gap-1.5 flex-shrink-0">
			<Icon className="w-3 h-3" /> {label}
		</span>
		{children ?? <span className="font-medium text-text text-right truncate">{value}</span>}
	</div>
);

DrawerField.propTypes = {
	icon: PropTypes.elementType.isRequired,
	label: PropTypes.string.isRequired,
	value: PropTypes.string,
	children: PropTypes.node,
};

// Company Drawer (loads details lazily)

const CompanyDrawer = ({ companyId, dealStats, onClose, onNavigate, onAddContact }) => {
	const { data, isLoading } = useCompanyDetail(companyId);

	const company = data?.company;
	const deals = data?.deals ?? [];
	const contacts = data?.contacts ?? [];
	const tags = data?.tags ?? [];

	return (
		<aside className="w-[360px] flex-shrink-0 border-l border-bgBase-500 bg-white overflow-y-auto">
			<div className="flex items-center justify-between px-5 py-3.5 border-b border-bgBase-400">
				<div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[12px] font-semibold bg-primary-100 text-primary-700">
					<BsBuilding className="w-3.5 h-3.5" /> Unternehmen
				</div>
				<IconButton icon={BsX} label="Schließen" onClick={onClose} />
			</div>

			{isLoading && <div className="px-5 py-8 text-center text-sm text-bgBase-800">Wird geladen …</div>}

			{!isLoading && company && (
				<>
					<div className="flex items-start gap-3 px-5 py-4">
						<div className="w-10 h-10 rounded-control bg-gradient-to-br from-primary-400 to-primary-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
							{getInitials(company.companyName)}
						</div>
						<div className="min-w-0 flex-1">
							<h2 className="text-md font-bold text-text truncate">{company.companyName}</h2>
							<div className="text-[11px] text-bgBase-800 flex items-center gap-1.5 mt-0.5 flex-wrap">
								{company.addressCity && (
									<span className="flex items-center gap-0.5">
										<BsGeoAlt className="w-2.5 h-2.5" /> {company.addressCity}
									</span>
								)}
							</div>
						</div>
					</div>

					{tags.length > 0 && (
						<div className="flex flex-wrap gap-1 px-5 pb-3">
							{tags.map((tag) => (
								<Tag key={tag.id} label={tag.name} size="xs" />
							))}
						</div>
					)}

					<div className="grid grid-cols-3 gap-1.5 px-5 pb-4">
						{company.phoneNumber && (
							<a
								href={`tel:${company.phoneNumber}`}
								className="inline-flex items-center justify-center gap-1 text-[11px] font-medium text-text border border-bgBase-500 rounded-control px-2.5 py-2 hover:bg-bgBase-300 transition-colors"
							>
								<BsTelephone className="w-3 h-3" /> Anrufen
							</a>
						)}
						{company.emailAddress && (
							<a
								href={`mailto:${company.emailAddress}`}
								className="inline-flex items-center justify-center gap-1 text-[11px] font-medium text-text border border-bgBase-500 rounded-control px-2.5 py-2 hover:bg-bgBase-300 transition-colors"
							>
								<BsEnvelope className="w-3 h-3" /> E-Mail
							</a>
						)}
						{company.websiteUrl && (
							<a
								href={
									company.websiteUrl.startsWith('http')
										? company.websiteUrl
										: `https://${company.websiteUrl}`
								}
								target="_blank"
								rel="noreferrer"
								className="inline-flex items-center justify-center gap-1 text-[11px] font-medium text-text border border-bgBase-500 rounded-control px-2.5 py-2 hover:bg-bgBase-300 transition-colors"
							>
								<BsGlobe className="w-3 h-3" /> Website
							</a>
						)}
					</div>

					{dealStats && (
						<div className="px-5 pb-4">
							<div className="grid grid-cols-3 gap-2">
								<div className="bg-bgBase-300 rounded-card p-2.5 text-center">
									<div className="text-sm font-bold text-text">{dealStats.dealCount}</div>
									<div className="text-[10px] text-bgBase-800 font-medium">Deals</div>
								</div>
								<div className="bg-bgBase-300 rounded-card p-2.5 text-center">
									<div className="text-sm font-bold text-success-700">{dealStats.openCount}</div>
									<div className="text-[10px] text-bgBase-800 font-medium">Offen</div>
								</div>
								<div className="bg-bgBase-300 rounded-card p-2.5 text-center">
									<div className="text-sm font-bold text-text">
										{formatEuro(dealStats.totalValue)}
									</div>
									<div className="text-[10px] text-bgBase-800 font-medium">Pipeline</div>
								</div>
							</div>
						</div>
					)}

					<div className="px-5 pb-4 border-t border-bgBase-400 pt-4">
						<h4 className="text-[11px] font-bold uppercase tracking-wider text-bgBase-800 mb-2.5">
							Stammdaten
						</h4>
						<div className="flex flex-col gap-2">
							{company.addressStreet && (
								<DrawerField
									icon={BsGeoAlt}
									label="Adresse"
									value={[company.addressStreet, company.addressPostal, company.addressCity]
										.filter(Boolean)
										.join(', ')}
								/>
							)}
							{company.phoneNumber && (
								<DrawerField icon={BsTelephone} label="Telefon">
									<a
										href={`tel:${company.phoneNumber}`}
										className="text-xs font-medium text-primary-600 hover:text-primary-700"
									>
										{company.phoneNumber}
									</a>
								</DrawerField>
							)}
							{company.emailAddress && (
								<DrawerField icon={BsEnvelope} label="E-Mail">
									<a
										href={`mailto:${company.emailAddress}`}
										className="text-xs font-medium text-primary-600 hover:text-primary-700 truncate"
									>
										{company.emailAddress}
									</a>
								</DrawerField>
							)}
							{company.websiteUrl && (
								<DrawerField icon={BsGlobe} label="Website">
									<a
										href={
											company.websiteUrl.startsWith('http')
												? company.websiteUrl
												: `https://${company.websiteUrl}`
										}
										target="_blank"
										rel="noreferrer"
										className="text-xs font-medium text-primary-600 hover:text-primary-700 truncate flex items-center gap-1"
									>
										{company.websiteUrl.replace(/^https?:\/\//, '')}{' '}
										<BsArrowRight className="w-2.5 h-2.5" />
									</a>
								</DrawerField>
							)}
						</div>
					</div>

					{company.customFields && Object.keys(company.customFields).length > 0 && (
						<div className="px-5 pb-4 border-t border-bgBase-400 pt-4">
							<h4 className="text-[11px] font-bold uppercase tracking-wider text-bgBase-800 mb-2.5">
								Benutzerdefinierte Felder
							</h4>
							<CustomFieldsSection entityType="company" values={company.customFields} readOnly />
						</div>
					)}

					<div className="px-5 pb-4 border-t border-bgBase-400 pt-4">
						<div className="flex items-center justify-between mb-2.5">
							<h4 className="text-[11px] font-bold uppercase tracking-wider text-bgBase-800">
								Kontakte ({contacts.length})
							</h4>
							<button
								type="button"
								onClick={() => onAddContact?.(companyId)}
								className="inline-flex items-center gap-1 text-[11px] text-bgBase-800 hover:text-primary-600 transition-colors"
							>
								<BsPlus className="w-3.5 h-3.5" /> Kontakt
							</button>
						</div>
						{contacts.length === 0 && (
							<p className="text-xs text-bgBase-800 italic">Keine Kontakte hinterlegt.</p>
						)}
						<div className="flex flex-col gap-1.5">
							{contacts.map((contact) => {
								const displayName =
									contact.fullName ||
									[contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
									contact.emailAddress ||
									'–';
								return (
									<div
										key={contact.id}
										className="flex items-center gap-2.5 p-2 rounded-card hover:bg-bgBase-300 transition-colors"
									>
										<div className="w-7 h-7 rounded-full bg-primary-500 text-white flex items-center justify-center text-[9px] font-bold flex-shrink-0">
											{displayName[0]?.toUpperCase() ?? '?'}
										</div>
										<div className="flex-1 min-w-0">
											<div className="text-[12.5px] font-semibold text-text truncate">
												{displayName}
												{contact.isPrimary && (
													<span className="ml-1.5 text-[9px] font-semibold text-caution-700 bg-caution-200 rounded-full px-1 py-0.5 uppercase">
														Primary
													</span>
												)}
											</div>
											{contact.companyRole && (
												<div className="text-[11px] text-bgBase-800 truncate">
													{getGermanLabel(contact.companyRole)}
												</div>
											)}
										</div>
										<div className="flex items-center gap-1 flex-shrink-0">
											{contact.emailAddress && (
												<a
													href={`mailto:${contact.emailAddress}`}
													onClick={(e) => e.stopPropagation()}
													className="w-6 h-6 rounded-control text-bgBase-800 hover:text-primary-600 hover:bg-primary-100 flex items-center justify-center transition-colors"
													title={contact.emailAddress}
												>
													<BsEnvelope className="w-3 h-3" />
												</a>
											)}
											{contact.phoneNumber && (
												<a
													href={`tel:${contact.phoneNumber}`}
													onClick={(e) => e.stopPropagation()}
													className="w-6 h-6 rounded-control text-bgBase-800 hover:text-primary-600 hover:bg-primary-100 flex items-center justify-center transition-colors"
													title={contact.phoneNumber}
												>
													<BsTelephone className="w-3 h-3" />
												</a>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</div>

					{deals.length > 0 && (
						<div className="px-5 pb-4 border-t border-bgBase-400 pt-4">
							<h4 className="text-[11px] font-bold uppercase tracking-wider text-bgBase-800 mb-2.5">
								Deals ({deals.length})
							</h4>
							<div className="flex flex-col gap-1.5">
								{deals.map((deal) => (
									<div
										key={deal.id}
										className="flex items-center gap-2.5 p-2 rounded-card bg-bgBase-300 hover:bg-bgBase-400 transition-colors"
									>
										<div className="flex-1 min-w-0">
											<div className="text-[12px] font-semibold text-text truncate">
												{deal.title}
											</div>
											<div className="flex items-center gap-2 mt-0.5">
												<span
													title={
														deal.status === 'lost'
															? (formatLossReason(deal) ?? undefined)
															: undefined
													}
													className={`text-[10px] uppercase font-medium px-1.5 py-0.5 rounded-full ${
														deal.status === 'open'
															? 'bg-success-200 text-success-700'
															: deal.status === 'won'
																? 'bg-primary-200 text-primary-700'
																: deal.status === 'lost'
																	? 'bg-warning-200 text-warning-700'
																	: 'bg-bgBase-400 text-bgBase-800'
													}`}
												>
													{deal.status === 'open'
														? 'Offen'
														: deal.status === 'won'
															? 'Gewonnen'
															: deal.status === 'lost'
																? 'Verloren'
																: deal.status}
												</span>
												{deal.dealValue > 0 && (
													<span className="text-[11px] font-semibold text-text">
														{formatEuro(deal.dealValue)}
													</span>
												)}
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
					)}

					<div className="px-5 pb-5 border-t border-bgBase-400 pt-4">
						<Button onClick={onNavigate} variant="outline" size="sm" fullWidth>
							Details öffnen <BsArrowRight className="w-3 h-3" />
						</Button>
					</div>
				</>
			)}
		</aside>
	);
};

CompanyDrawer.propTypes = {
	companyId: PropTypes.string.isRequired,
	dealStats: PropTypes.shape({
		dealCount: PropTypes.number,
		openCount: PropTypes.number,
		totalValue: PropTypes.number,
	}),
	onClose: PropTypes.func.isRequired,
	onNavigate: PropTypes.func.isRequired,
	onAddContact: PropTypes.func,
};

export default CompanyDrawer;
